import { engineComponent } from "neo-common";
import { Communication } from "neo-games";
import { Payline } from "../GameComponents/Payline";
import { Symbol } from "../GameComponents/Symbol";
import { WinAnimator } from "../GameComponents/WinAnimator";
import { ComponentTypes } from "../Components/ComponentTypes";
import { AnimationElement } from "../Components/Elements/Graphics/AnimationElement";
import { Tweens } from "../Components/Effects/Tweens";
import { MachineConfigurationAttributes, ReelConfigurationAttributes } from "../Components/Configuration";
import { PlayList } from "../Components/Sounds/PlayList";
import { DataManager } from "../Managers/DataManager";
import { GameData } from "../Channels/Common/Datas/GameData";

/**
 * WinAnimatorExtended extends WinAnimator which uses paylines and symbols to create presentation of wins to user.
 * Also handle high wins animation for presentation special screens to user when win is bigger than some defined values.
 * @class WinAnimatorExtended
 * @extends WinAnimator
 */
@engineComponent(ComponentTypes.DisplayObject)
export class WinAnimatorExtended extends WinAnimator {
    protected elementInstance: Array<Object> = [];

    // pre roll height in pixels
    private _preRoll: number = 180;
    // pre roll speed in frames
    private _preRollSpeed: number = 10;
    // delay to start rotation in frames
    private _startDelay: number = 3;

    public inFreeSpins: boolean;

    //******************************************************************************************************************
    //  EVENTS
    //******************************************************************************************************************

    public static readonly StartFreeSpins: string = "startFreespins";

    public static readonly WonNewFreeSpins: string = "wonNewFreespins";

    //******************************************************************************************************************
    //  CREATE, INITIALIZE
    //******************************************************************************************************************

    protected configure(node: Element): void {
        super.configure(node);

        if (node.hasAttribute(ReelConfigurationAttributes.PRE_ROLL)) {
            this._preRoll = parseInt(node.getAttribute(ReelConfigurationAttributes.PRE_ROLL));
        }

        if (node.hasAttribute(ReelConfigurationAttributes.PRE_ROLL_SPEED)) {
            this._preRollSpeed = parseInt(node.getAttribute(ReelConfigurationAttributes.PRE_ROLL_SPEED));
        }

        if (node.hasAttribute(MachineConfigurationAttributes.START_DELAY)) {
            this._startDelay = parseInt(node.getAttribute(MachineConfigurationAttributes.START_DELAY));
        }
    }

    /**
     *
     * @param {Object} element
     */
    public addElementInstance(element: Object): void {
        this.elementInstance[Object.keys(element)[0]] = element[Object.keys(element)[0]];
    }

    //******************************************************************************************************************
    //  ON REEL STOPPED, SHOW SYMBOLS
    //******************************************************************************************************************

    public onReelStopped(reelsData: Array<Communication.Game.IReel>, reelIndex: number, force: boolean = false): void {
        super.onReelStopped(reelsData, reelIndex);

        if (!force) {
            this.tease(reelIndex);
        }
    }

    private tease(reelIndex: number): void {
        const count = this.calculateScatters(reelIndex);

        switch (count) {
            case 0:
                return;
            case 1: // 1 scatter on scene
                this.playScatterSoundBasedOnCount(reelIndex, 1);
                this.tease1(reelIndex);
                return;
            case 2: // 2 scatters on scene
                this.playScatterSoundBasedOnCount(reelIndex, 2);
                this.tease2(reelIndex);
                return;
            case 3: // 3 scatters on scene
                this.playScatterSoundBasedOnCount(reelIndex, 3);
                this.tease3(reelIndex);
                return;
            case 4: // 4 scatters on scene
                this.playScatterSoundBasedOnCount(reelIndex, 4);
                this.tease3(reelIndex);
                return;
            default: // More than 4 scatters on scene
                this.playScatterSoundBasedOnCount(reelIndex, 5);
                this.tease3(reelIndex);
                return;
        }
    }

    private playScatterSoundBasedOnCount(reelIndex: number, count: number): void {
        if ((reelIndex === 3 && count < 2) || (reelIndex === 4 && count < 3)) {
            return;
        }

        if (this.currentReels[reelIndex].symbols.indexOf(11) != -1) {
            PlayList.play("SND_SymScatterStop0" + count);
        }
    }

    /**
     * Return count of scatters on reels [0..reelIndex]
     *
     * @param {number} reelIndex
     * @returns {number}
     */
    private calculateScatters(reelIndex: number): number {
        let count = 0;

        for (let i = 0; i <= reelIndex; i++) {
            if (this.currentReels[i].symbols.indexOf(11) != -1) {
                count++;
            }
        }

        return count;
    }

    private tease1(reelIndex: number): void {
        if (reelIndex >= 3) {
            return;
        }

        const symbolIndex = this.currentReels[reelIndex].symbols.indexOf(11);

        if (symbolIndex != -1) {
            this.currentSymbols[reelIndex][symbolIndex].goToCase(Symbol.CaseImpact);
        }
    }

    private tease2(reelIndex: number): void {
        if (reelIndex >= 4) {
            for (let i = 0; i < this.currentSymbols.length; i++) {
                for (let j = 0; j < this.currentSymbols[i].length; j++) {
                    const symbol = this.currentSymbols[i][j];

                    if (symbol.id !== 11) {
                        symbol.goToCase(Symbol.CaseNormal);
                    }
                }
            }

            return;
        }

        if (this.currentReels[reelIndex].symbols.indexOf(11) !== -1) {
            for (let i = 0; i < reelIndex; i++) {
                for (let j = 0; j < this.currentSymbols[i].length; j++) {
                    if (this.currentSymbols[i][j].id === 10) {
                        this.currentSymbols[i][j].goToCase(Symbol.CaseTeaser);
                    } else if (this.currentSymbols[i][j].id !== 11) {
                        this.currentSymbols[i][j].goToCase(Symbol.CaseNowin);
                    }
                }
            }
        }

        this.tease3(reelIndex);
    }

    /**
     * Prechadza cez vsetky symboly v danom valci
     * @param {number} reelIndex
     */
    private tease3(reelIndex: number): void {
        for (let i = 0; i < this.currentSymbols[reelIndex].length; i++) {
            if (this.currentSymbols[reelIndex][i].id === 10) {
                this.currentSymbols[reelIndex][i].goToCase(Symbol.CaseImpactTease);
            } else if (this.currentSymbols[reelIndex][i].id !== 11) {
                this.currentSymbols[reelIndex][i].goToCase(Symbol.CaseNowin);
            }
        }
    }

    public removeTeasingFromScatters(symbolId = 11): void {
        if (this.currentSymbols != null) {
            for (let reelIndex in this.currentSymbols) {
                for (let rowIndex = 0; rowIndex < this.currentSymbols[reelIndex].length; rowIndex++) {
                    const symbol = this.currentSymbols[reelIndex][rowIndex];

                    if (symbol != null && symbol.id !== 11) {
                        this.currentSymbols[reelIndex][rowIndex].goToCase(Symbol.CaseNormal);
                    }
                }
            }
        }
    }

    //******************************************************************************************************************
    //  SHOW WINS
    //******************************************************************************************************************

    public showWins(wins: Array<Communication.Game.IWin>, win: number): boolean {
        AnimationElement.DynamicTint = this.inFreeSpins ? "ffffff" : "ffff00";

        return super.showWins(wins, win);
    }

    protected finishFirstWins(): void {
        for (let i = 0; i < this.currentWins.length; i++) {
            this.winSymbolsGoToCase(i, Symbol.CaseWinEnd, false);
        }

        this.emit(WinAnimator.FinishWinAnimator);

        this.currentCycle = 0;

        if (this.currentWins != null) {
            if (this.currentWins.length === 1) {
                this.allPaylinesGoToCase(Payline.CaseNowin);
                this.winSymbolsGoToCase(0, Symbol.CaseSingleWin, true);
                this.playSoundBasedOnPayout(this.currentWins[0].payoutId);
                this.winPaylineGoToCase(0, Payline.CaseSingleWin);
            } else if (this.currentWins.length === 2) {
                let hasScatterFreespinWin: boolean = false;
                let indexScatterFreespinWin: number = -1;
                for (let i = 0; i < this.currentWins.length; i++) {
                    if (
                        this.currentWins[i].trigger === Communication.Game.WinTrigger.Scatter &&
                        this.currentWins[i].type === Communication.Game.WinType.freeSpins
                    ) {
                        hasScatterFreespinWin = true;
                        indexScatterFreespinWin = i;
                    }
                }

                if (hasScatterFreespinWin) {
                    // If one win from 2 wins is freespinsWin, animate only non freespinsWin
                    this.allPaylinesGoToCase(Payline.CaseNowin);
                    this.winSymbolsGoToCase(indexScatterFreespinWin ? 0 : 1, Symbol.CaseSingleWin, true);
                    this.playSoundBasedOnPayout(this.currentWins[indexScatterFreespinWin ? 0 : 1].payoutId);
                    this.winPaylineGoToCase(indexScatterFreespinWin ? 0 : 1, Payline.CaseSingleWin);
                } else {
                    this.cycleWins();
                }
            } else {
                this.cycleWins();
            }
        }
    }

    //******************************************************************************************************************
    //  SHOW INIT WINS
    //******************************************************************************************************************

    public showInitWins(wins: Array<Communication.Game.IWin>, win: number): void {
        AnimationElement.DynamicTint = this.inFreeSpins ? "ffffff" : "ffff00";

        super.showInitWins(wins, win);
    }

    /**
     * Overrides method showFirstInitWins() because Symbol goes to CaseWinEnd.
     */
    protected showFirstInitWins(): void {
        this.allSymbolsGoToCase(Symbol.CaseNowin, true);
        this.allPaylinesGoToCase(Payline.CaseNowin);

        for (let i = 0; i < this.currentWins.length; i++) {
            this.winSymbolsGoToCase(i, Symbol.CaseWinEnd, false);
            this.winPaylineGoToCase(i, Payline.CaseFirstWin);
        }
    }

    protected finishFirstInitWins(): void {
        this.currentCycle = 0;

        if (this.currentWins != null) {
            if (this.currentWins.length === 1) {
                this.allPaylinesGoToCase(Payline.CaseNowin);
                this.winSymbolsGoToCase(0, Symbol.CaseSingleWin, true);
                this.playSoundBasedOnPayout(this.currentWins[0].payoutId);
                this.winPaylineGoToCase(0, Payline.CaseSingleWin);
            } else if (this.currentWins.length === 2) {
                let hasScatterFreespinWin: boolean = false;
                let indexScatterFreespinWin: number = -1;
                for (let i = 0; i < this.currentWins.length; i++) {
                    if (
                        this.currentWins[i].trigger === Communication.Game.WinTrigger.Scatter &&
                        this.currentWins[i].type === Communication.Game.WinType.freeSpins
                    ) {
                        hasScatterFreespinWin = true;
                        indexScatterFreespinWin = i;
                    }
                }

                if (hasScatterFreespinWin) {
                    // If one win from 2 wins is freespinsWin, animate only non freespinsWin
                    this.allPaylinesGoToCase(Payline.CaseNowin);
                    this.winSymbolsGoToCase(indexScatterFreespinWin ? 0 : 1, Symbol.CaseSingleWin, true);
                    this.playSoundBasedOnPayout(this.currentWins[indexScatterFreespinWin ? 0 : 1].payoutId);
                    this.winPaylineGoToCase(indexScatterFreespinWin ? 0 : 1, Payline.CaseSingleWin);
                } else {
                    this.cycleWins();
                }
            } else {
                this.cycleWins();
            }
        }
    }

    //******************************************************************************************************************
    //  CYCLE WINS
    //******************************************************************************************************************

    protected cycleWins(): void {
        if (this.currentWins == null) {
            return;
        }

        AnimationElement.DynamicTint = this.inFreeSpins ? "ffffff" : "ffff00";

        this.allPaylinesGoToCase(Payline.CaseNowin);

        for (let i = 0; i < this.currentWins.length; i++) {
            this.winPaylineGoToCase(i, Payline.CaseNormal);
        }

        let skipCycle: boolean = false;

        if (
            this.currentWins[this.currentCycle].trigger === Communication.Game.WinTrigger.Scatter &&
            this.currentWins[this.currentCycle].type === Communication.Game.WinType.freeSpins
        ) {
            skipCycle = true;
        } else {
            this.winSymbolsGoToCase(this.currentCycle, Symbol.CaseCycle, true);
            this.winPaylineGoToCase(this.currentCycle, Payline.CaseWin);
            this.playSoundBasedOnPayout(this.currentWins[this.currentCycle].payoutId);
        }

        this.currentCycle = this.currentCycle + 1;

        if (this.currentCycle >= this.currentWins.length) {
            this.currentCycle = 0;
        }

        if (skipCycle) {
            this.startTimer(WinAnimator.CycleWins, 1);
        } else {
            this.startTimer(WinAnimator.CycleWins, 120);
        }
    }

    //******************************************************************************************************************
    //  RESET
    //******************************************************************************************************************

    public reset(showLastSymbols: boolean, spin: boolean = false): boolean {
        if (spin) {
            for (let reelIndex = 0; reelIndex < this.currentSymbols.length; reelIndex++) {
                for (let rowIndex = 0; rowIndex < this.currentSymbols[reelIndex].length; rowIndex++) {
                    const symbol = this.currentSymbols[reelIndex][rowIndex];

                    if (symbol.id === 10 || symbol.id === 11) {
                        this.currentSymbols[reelIndex][rowIndex] = null;
                        this.elementInstance["wildContainer"].addChild(symbol);

                        if (symbol.id === 10) {
                            symbol.goToCase(Symbol.CaseNormal);
                        }

                        Tweens.positionTween(symbol, {
                            tweenDelay: this._startDelay * reelIndex,
                            tweenX: [symbol.x, symbol.x, symbol.x],
                            tweenY: [symbol.y, symbol.y - this._preRoll, symbol.y],
                            tweenDuration: this._preRollSpeed * 2,
                            callbackFunction: (): void => {
                                this.symbols.returnSymbol(symbol);
                            },
                        });
                    }
                }
            }
        }

        return super.reset(showLastSymbols);
    }

    //******************************************************************************************************************
    //  SYMBOLS
    //******************************************************************************************************************

    protected addCurrentSymbol(reelIndex: number, symbolIndex: number): void {
        super.addCurrentSymbol(reelIndex, symbolIndex);

        this.sortChildrenByZindex();

        if (this.inFreeSpins && this.currentReels[reelIndex].symbols[symbolIndex] === 12) {
            if (DataManager.getDataById(GameData.DataFreeSpinsMultiplierIncreased) === "true") {
                this.currentSymbols[reelIndex][symbolIndex].goToCase(Symbol.CaseWinMultiplier);

                PlayList.play("SND_Sym12sun");
            } else {
                PlayList.play("SND_Sym12sunStop");
            }
        }
    }

    protected winSymbolsGoToCase(winIndex: number, caseType: string, useValue: boolean): void {
        super.winSymbolsGoToCase(winIndex, caseType, useValue);

        this.sortChildrenByZindex();
    }

    protected allowSymbolGoToCase(reelIndex: number, symbolIndex: number, caseType: string): boolean {
        return !(
            this.inFreeSpins &&
            caseType === Symbol.CaseNowin &&
            this.currentReels[reelIndex].symbols[symbolIndex] === 12 &&
            DataManager.getDataById(GameData.DataFreeSpinsMultiplierIncreased) === "true"
        );
    }

    /**
     * Sorts children of container by symbols zIndex attribute
     */
    protected sortChildrenByZindex(): void {
        this.symbolsContainer.children.sort((a: any, b: any) => {
            if (a.hasOwnProperty("zIndex") && b.hasOwnProperty("zIndex")) {
                a.zIndex = a.zIndex || 0;
                b.zIndex = b.zIndex || 0;

                return a.zIndex - b.zIndex;
            }

            return 0;
        });
    }

    private playSoundBasedOnPayout(payoutId: number = -1): void {
        let id: number;

        if (payoutId !== -1) {
            id = payoutId;
        } else {
            let highestWin: Communication.Game.IWin = null;

            for (let i: number = 0; i < this.currentWins.length; i++) {
                if (highestWin == null) {
                    highestWin = this.currentWins[i];
                } else {
                    if (highestWin.amount > this.currentWins[i].amount) {
                        highestWin = this.currentWins[i];
                    }
                }
            }

            if (highestWin != null && highestWin.payoutId != null) {
                id = highestWin.payoutId;
            }
        }

        if (id != null) {
            switch (id) {
                /*case 1:
                    PlayList.play('SND_BaseWinline');
                    break;
                case 2:
                    PlayList.play('SND_BaseWinline');
                    break;
                case 3:
                    PlayList.play('SND_BaseWinline');
                    break;
                case 4:
                    PlayList.play('SND_BaseWinline');
                    break;
                case 5:
                    PlayList.play('SND_BaseWinline');
                    break;*/
                case 6:
                    PlayList.play("SND_Sym06antelope");
                    break;
                case 7:
                    PlayList.play("SND_Sym07giraffe");
                    break;
                case 8:
                    PlayList.play("SND_Sym08elephant");
                    break;
                case 9:
                    PlayList.play("SND_Sym09lion");
                    break;
                case 10:
                    PlayList.play("SND_Sym10mask");
                    break;
                case 11:
                    PlayList.play("SND_Sym11kilimanjaro");
                    break;
            }
        }
    }

    //******************************************************************************************************************
    //  IDLE
    //******************************************************************************************************************

    protected idle(): void {}
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/WinAnimatorExtended.ts
 **/